import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Ruler } from 'lucide-react';
import { createUnit, updateUnit, deleteUnit, seedUnits } from '@/lib/api';

export const UnitsTab = ({ units, onReload, isAdmin }) => {
  const { t } = useTranslation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editUnit, setEditUnit] = useState(null);
  const [form, setForm] = useState({ code: '', name_de: '', name_en: '', sort_order: 0, active: true });
  const [saving, setSaving] = useState(false);

  const openCreate = () => {
    setEditUnit(null);
    setForm({ code: '', name_de: '', name_en: '', sort_order: units.length, active: true });
    setDialogOpen(true);
  };

  const openEdit = (unit) => {
    setEditUnit(unit);
    setForm({ code: unit.code, name_de: unit.name_de, name_en: unit.name_en, sort_order: unit.sort_order, active: unit.active });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error(t('admin.units.codeRequired')); return; }
    setSaving(true);
    try {
      if (editUnit) {
        await updateUnit(editUnit.unit_id, form);
        toast.success(t('admin.units.updated'));
      } else {
        await createUnit(form);
        toast.success(t('admin.units.created'));
      }
      setDialogOpen(false);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.common.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (unit) => {
    if (!window.confirm(t('admin.units.confirmDelete', { code: unit.code }))) return;
    try {
      await deleteUnit(unit.unit_id);
      toast.success(t('admin.units.deleted'));
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.common.deleteError'));
    }
  };

  const handleSeed = async () => {
    try {
      const res = await seedUnits();
      toast.success(res.data.message);
      onReload();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('admin.common.saveError'));
    }
  };

  return (
    <Card data-testid="units-tab">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Ruler className="h-5 w-5 text-orange-500" />
          {t('admin.units.manage')}
        </CardTitle>
        <div className="flex gap-2">
          {units.length === 0 && (
            <Button variant="outline" size="sm" onClick={handleSeed} data-testid="seed-units-btn">
              {t('admin.units.loadDefaults')}
            </Button>
          )}
          {isAdmin && (
            <Button size="sm" onClick={openCreate} data-testid="add-unit-btn">
              <Plus className="h-4 w-4 mr-1" /> {t('admin.units.newUnit')}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {units.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            {t('admin.units.noUnitsHint')}
          </p>
        ) : (
          <Table data-testid="units-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.units.code')}</TableHead>
                <TableHead>{t('admin.units.nameDe')}</TableHead>
                <TableHead>{t('admin.units.nameEn')}</TableHead>
                <TableHead>{t('admin.units.sortOrder')}</TableHead>
                <TableHead>{t('admin.units.status')}</TableHead>
                {isAdmin && <TableHead className="w-24">{t('admin.units.actions')}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {units.map(unit => (
                <TableRow key={unit.unit_id} data-testid={`unit-row-${unit.code}`}>
                  <TableCell className="font-mono font-medium">{unit.code}</TableCell>
                  <TableCell>{unit.name_de}</TableCell>
                  <TableCell>{unit.name_en}</TableCell>
                  <TableCell>{unit.sort_order}</TableCell>
                  <TableCell>
                    <Badge variant={unit.active ? 'default' : 'secondary'}>
                      {unit.active ? t('admin.units.active') : t('admin.units.inactive')}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(unit)} data-testid={`edit-unit-${unit.code}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:text-red-700" onClick={() => handleDelete(unit)} data-testid={`delete-unit-${unit.code}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent data-testid="unit-dialog">
          <DialogHeader>
            <DialogTitle>{editUnit ? t('admin.units.editUnit') : t('admin.units.newUnit')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>{t('admin.units.code')} *</Label>
              <Input value={form.code} onChange={(e) => setForm(p => ({ ...p, code: e.target.value }))}
                placeholder="kg, pcs, m2" data-testid="unit-code-input" disabled={!!editUnit} />
            </div>
            <div>
              <Label>{t('admin.units.nameDe')}</Label>
              <Input value={form.name_de} onChange={(e) => setForm(p => ({ ...p, name_de: e.target.value }))}
                data-testid="unit-name-de-input" />
            </div>
            <div>
              <Label>{t('admin.units.nameEn')}</Label>
              <Input value={form.name_en} onChange={(e) => setForm(p => ({ ...p, name_en: e.target.value }))}
                data-testid="unit-name-en-input" />
            </div>
            <div>
              <Label>{t('admin.units.sortOrder')}</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))}
                data-testid="unit-sort-input" />
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm(p => ({ ...p, active: v }))} data-testid="unit-active-switch" />
              <Label>{t('admin.units.active')}</Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>{t('common.cancel')}</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="unit-save-btn">
                {saving ? t('admin.units.saving') : t('common.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
